import { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext, useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import LiveCard from "@/components/LiveCard";
import LiveRoom from "@/components/LiveRoom";
import { LIVE } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Radio, Eye, Rows3, LayoutGrid, Sparkles, X, BadgeCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import VerticalLiveFeed from "@/components/VerticalLiveFeed";
import VerifiedBadge from "@/components/VerifiedBadge";
import useLivePreview from "@/hooks/useLivePreview";

const CATEGORIES = ["All", "Just Chatting", "Late Night", "Music", "Fitness"];

/** Featured hero live card — mirrors the top card on the mobile app's "The Stage". */
function StageHero({ stream, onOpen }) {
  const { ref, src, onError } = useLivePreview(stream);
  return (
    <button
      type="button"
      ref={ref}
      onClick={() => onOpen(stream)}
      data-testid={`stage-hero-${stream.id}`}
      className="relative w-full aspect-[16/11] card-surface overflow-hidden group text-left"
    >
      {src ? (
        <img src={src} onError={onError} alt={stream.title || "Untitled Live"} className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-rose-900/40 via-black to-black" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
      <div className="absolute top-4 left-4 flex items-center gap-1.5 px-2.5 py-1 bg-rose-600 text-white text-[10px] uppercase tracking-widest font-bold rounded-full">
        <span className="w-1.5 h-1.5 rounded-full bg-white pulse-dot" /> Live
      </div>
      <div className="absolute top-4 right-4 flex items-center gap-1 px-2.5 py-1 bg-black/70 backdrop-blur text-white text-xs rounded-full">
        <Eye size={12} /> {(stream.viewer_count || 0).toLocaleString()}
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-5">
        <div className="font-display text-2xl sm:text-3xl text-white leading-tight mb-3 line-clamp-2 drop-shadow">
          {stream.title || "Untitled Live"}
        </div>
        <div className="flex items-center gap-2">
          <Avatar className="w-8 h-8 border border-white/30">
            <AvatarImage src={stream.host?.avatar_url} />
            <AvatarFallback className="bg-rose-600 text-white text-xs">
              {stream.host?.display_name?.[0]?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <span className="text-sm text-white/90 font-medium">{stream.host?.display_name}</span>
          <VerifiedBadge show={stream.host?.is_verified} size="sm" />
          {stream.category && (
            <span className="ml-1 text-[10px] uppercase tracking-widest bg-white/10 backdrop-blur border border-white/15 rounded-full px-2 py-1 text-white/90">
              {stream.category}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

export default function LivePage() {
  const { user } = useAuth();
  const { openGoLive } = useOutletContext() || {};
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const directFetchRef = useRef(null);
  const [streams, setStreams] = useState([]);
  const [active, setActive] = useState(null);
  // null = unknown (fetching/unreachable); only a SUCCESSFUL response may set
  // {configured:false} — a failed fetch must never show the demo-mode banner.
  const [config, setConfig] = useState(null);
  const configRef = useRef(null);
  useEffect(() => { configRef.current = config; });
  const [category, setCategory] = useState("All");
  // SECURITY NOTE (for scanners): the two localStorage keys used below
  // (`pandax_surf_mode` and `pandax_stage_desires`) store only UI
  // preferences — a boolean grid/vertical toggle and an array of tag
  // strings the user picked to filter Live discovery. No auth tokens, no
  // PII, no message content, no payment/session data. Safe in localStorage.
  const [verifiedOnly, setVerifiedOnly] = useState(() => { try { return window.localStorage.getItem("pandax_live_verified_only") === "1"; } catch { return false; } });
  const [surfMode, setSurfMode] = useState(
    typeof window !== "undefined" && window.localStorage.getItem("pandax_surf_mode") === "1"
  );
  const [desireFilters, setDesireFilters] = useState(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem("pandax_stage_desires");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [desireCatalog, setDesireCatalog] = useState([]);
  const [desireDrawerOpen, setDesireDrawerOpen] = useState(false);

  const load = () => {
    const params = {};
    if (desireFilters.length > 0) params.desires = desireFilters.join(",");
    return api.get("/live", { params }).then(({ data }) => setStreams(data)).catch(() => {});
  };
  // Ref keeps the WS effect below from binding to a stale `load` closure when
  // `desireFilters` changes: the WS handler reads loadRef.current, so live:start
  // / live:end events always trigger a reload with the LATEST filters, and we
  // don't have to tear the socket down on every filter toggle.
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; });

  useEffect(() => {
    load();
    // Config fetch with retry: 3 quick attempts with backoff, then the 15s
    // poll below keeps retrying until a successful response lands.
    const fetchConfig = (attempt = 0) => {
      api.get("/livekit/config")
        .then(({ data }) => setConfig(data))
        .catch(() => {
          if (attempt < 2) setTimeout(() => fetchConfig(attempt + 1), 2000 * (attempt + 1));
        });
    };
    fetchConfig();
    const onRefresh = () => loadRef.current?.();
    window.addEventListener("pandax:refresh-feed", onRefresh);
    const t = setInterval(() => {
      loadRef.current?.();
      if (configRef.current === null) fetchConfig();
    }, 15000);
    // Stage 3 — real-time WS. Reload the list on any live:start / live:end
    // so The Stage feels instant. Falls back silently to the 15 s poll if
    // the socket can't open (older proxies, restricted networks).
    let ws;
    try {
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const wsUrl = backend.replace(/^http/, "ws") + "/api/ws/live-status";
      ws = new WebSocket(wsUrl);
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.channel === "live:start" || msg.channel === "live:end") {
            loadRef.current?.();
          }
        } catch (parseErr) {
          // Malformed frame from live-status broadcaster — poll fallback
          // still refreshes the list every 30s, so this is non-fatal. Log
          // once per session to keep console noise down but preserve a
          // breadcrumb for ops if the backend ever emits bad JSON.
          if (!window.__pandax_ws_parse_warned) {
            window.__pandax_ws_parse_warned = true;
            console.warn("[Live] live-status WS frame parse failed:", parseErr);
          }
        }
      };
      ws.onerror = (err) => {
        console.warn("[Live] live-status WS error — polling fallback in use:", err);
      };
    } catch (wsErr) {
      // WebSocket construction itself failed (blocked by CSP, corporate
      // proxy, or the origin doesn't support upgrade). The 30s polling
      // interval above keeps discovery working, so we degrade quietly
      // rather than surfacing a scary toast to the user.
      console.warn("[Live] live-status WS unavailable, using poll only:", wsErr);
    }
    return () => {
      clearInterval(t);
      window.removeEventListener("pandax:refresh-feed", onRefresh);
      try {
        ws && ws.close();
      } catch {
        // idempotent close — WebSocket may already be in CLOSING/CLOSED
        // state, in which case .close() throws. Nothing to do.
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desireFilters]);

  useEffect(() => {
    api.get("/users/desires/catalog")
      .then(({ data }) => setDesireCatalog(Array.isArray(data?.tags) ? data.tags : []))
      .catch(() => setDesireCatalog([]));
  }, []);

  // Persist desire selection.
  useEffect(() => {
    try {
      window.localStorage.setItem("pandax_stage_desires", JSON.stringify(desireFilters));
    } catch { /* ignore */ }
  }, [desireFilters]);

  // Fix A — the host's just-created stream arrives via navigation state, so
  // the room opens instantly without depending on the discovery list, WS
  // event, or 15s poll (the list may exclude it: desire filters, iOS-shell
  // ios_safe filter, or staleness). The passed object is UI-only — role and
  // publish rights stay server-authoritative via the LiveKit token endpoint.
  // State is cleared immediately after consumption so back/refresh cannot
  // reopen a stale room from history state.
  useEffect(() => {
    const hs = location.state?.hostStream;
    if (!hs?.id) return;
    navigate(location.pathname + location.search, { replace: true, state: null });
    // POST /api/live returns the raw stream doc without a `host` block —
    // hydrate it from the authenticated user (display-only; server still
    // owns all rights) so the top bar doesn't render "@undefined".
    const hydrated = hs.host ? hs : {
      ...hs,
      host: {
        id: user?.id,
        handle: user?.handle,
        display_name: user?.display_name || user?.handle,
        avatar_url: user?.avatar_url,
        handle_hidden: !(user?.handle_public ?? true),
      },
    };
    setActive((cur) => cur || hydrated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  useEffect(() => {
    const wanted = params.get("s");
    if (!wanted || active) return;
    const s = streams.find((x) => x.id === wanted);
    if (s) {
      setActive((cur) => cur || s);
      return;
    }
    // Fix B — a valid ?s= that isn't in the (possibly filtered or stale)
    // discovery list: fetch the single stream through the authorized backend
    // route (enforces auth, privacy, blocked pairs, community access, and the
    // iOS-shell ios_safe rule server-side) and open it. The ref guards
    // against duplicate fetches when WS/poll updates re-run this effect.
    if (directFetchRef.current === wanted) return;
    directFetchRef.current = wanted;
    api.get(`/live/${wanted}`)
      .then(({ data }) => {
        if (data?.is_live) {
          setActive((cur) => cur || data);
        } else {
          toast.error("This stream has ended.");
          setParams({});
        }
      })
      .catch(() => {
        toast.error("Stream unavailable.");
        setParams({});
      });
  }, [params, streams, active, setParams]);

  const closeRoom = () => {
    directFetchRef.current = null;
    setActive(null);
    if (params.get("s")) setParams({});
    load();
  };

  const filtered = useMemo(() => {
    const base = verifiedOnly ? streams.filter((s) => s.host?.is_verified) : streams;
    if (category === "All") return base;
    return base.filter((s) => (s.category || "").toLowerCase() === category.toLowerCase());
  }, [streams, category, verifiedOnly]);
  const hero = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div data-testid={LIVE.root} className="p-6 lg:p-8">
      {/* Header — mirrors mobile app: "LIVE NOW" kicker + serif "The Stage" hero + live counter chip */}
      <header className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.32em] text-rose-500 mb-1">Live Now</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-none">The Stage</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end min-w-0">
          <button
            type="button"
            onClick={() => setDesireDrawerOpen((v) => !v)}
            data-testid="stage-desires-toggle"
            title="Filter by desire tags"
            className={`h-9 px-3 rounded-full text-xs uppercase tracking-widest border flex items-center gap-1.5 transition-colors ${
              desireFilters.length > 0
                ? "bg-rose-600/20 border-rose-500 text-rose-100"
                : "bg-black/30 border-white/15 text-zinc-300 hover:text-white"
            }`}
          >
            <Sparkles size={12} />
            {desireFilters.length > 0 ? `${desireFilters.length}` : "Filter"}
          </button>
          <button
            type="button"
            onClick={() => {
              const next = !verifiedOnly;
              setVerifiedOnly(next);
              try { window.localStorage.setItem("pandax_live_verified_only", next ? "1" : "0"); } catch { /* noop */ }
            }}
            data-testid="stage-verified-toggle"
            title={verifiedOnly ? "Showing Verified hosts only" : "Show Verified hosts only"}
            aria-pressed={verifiedOnly}
            className={`h-9 px-3 rounded-full text-xs uppercase tracking-widest border flex items-center gap-1.5 transition-colors ${
              verifiedOnly ? "bg-sky-600/25 border-sky-400 text-sky-100" : "bg-black/30 border-white/15 text-zinc-300 hover:text-white"
            }`}
          >
            <BadgeCheck size={12} className={verifiedOnly ? "fill-sky-500 text-white" : ""} />
            Verified
          </button>
          <button
            type="button"
            onClick={() => {
              const next = !surfMode;
              setSurfMode(next);
              try { window.localStorage.setItem("pandax_surf_mode", next ? "1" : "0"); } catch { /* noop */ }
            }}
            data-testid="stage-surf-toggle"
            title={surfMode ? "Switch to grid view" : "Switch to TikTok-style vertical feed"}
            className={`h-9 px-3 rounded-full text-xs uppercase tracking-widest border flex items-center gap-1.5 transition-colors ${
              surfMode
                ? "bg-rose-600 border-rose-600 text-white"
                : "bg-black/30 border-white/15 text-zinc-300 hover:text-white"
            }`}
          >
            {surfMode ? <LayoutGrid size={12} /> : <Rows3 size={12} />}
            {surfMode ? "Grid" : "Surf"}
          </button>
          <div
            className="flex items-center gap-1.5 text-white/90 text-sm font-medium bg-black/40 border border-white/10 rounded-full px-2.5 py-1"
            data-testid="stage-live-count"
            title={`${streams.length} live streams`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-rose-500 pulse-dot" />
            {streams.length}
          </div>
          {user && (
            <Button
              onClick={openGoLive}
              data-testid="stage-go-live"
              className="btn-crimson rounded-full h-9 px-4 flex items-center gap-2"
            >
              <Radio size={14} /> Go Live
            </Button>
          )}
        </div>
      </header>

      {/* Category pills — horizontal scroll on mobile */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 mb-5 -mx-1 px-1" data-testid="stage-categories">
        {CATEGORIES.map((c) => {
          const active = category === c;
          return (
            <button
              key={c}
              type="button"
              onClick={() => setCategory(c)}
              data-testid={`stage-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`shrink-0 rounded-full px-4 py-2 text-sm border transition-colors ${
                active
                  ? "bg-rose-600 border-rose-600 text-white"
                  : "bg-black/30 border-white/10 text-zinc-300 hover:border-white/25 hover:text-white"
              }`}
            >
              {c}
            </button>
          );
        })}
      </div>

      {desireDrawerOpen && (
        <div
          data-testid="stage-desires-drawer"
          className="card-surface p-4 mb-5 border-rose-500/30"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="font-display text-lg text-white flex items-center gap-2">
              <Sparkles size={16} className="text-rose-500" /> Filter The Stage by desires
            </div>
            <div className="flex items-center gap-2">
              {desireFilters.length > 0 && (
                <button
                  type="button"
                  onClick={() => setDesireFilters([])}
                  className="text-[11px] uppercase tracking-widest text-zinc-400 hover:text-rose-400"
                  data-testid="stage-desires-clear"
                >
                  Clear
                </button>
              )}
              <button
                type="button"
                onClick={() => setDesireDrawerOpen(false)}
                className="text-zinc-500 hover:text-white"
                aria-label="Close desire filter"
              >
                <X size={16} />
              </button>
            </div>
          </div>
          <p className="text-xs text-zinc-500 mb-3">
            Only shows live rooms whose host has picked at least one matching desire. Compatibility boost still floats ≥3-shared hosts to the top within the filter.
          </p>
          <div className="flex flex-wrap gap-1.5 max-h-64 overflow-y-auto" data-testid="stage-desires-picker">
            {desireCatalog.map((tag) => {
              const active = desireFilters.some((d) => d.toLowerCase() === tag.toLowerCase());
              return (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setDesireFilters((prev) =>
                    active
                      ? prev.filter((d) => d.toLowerCase() !== tag.toLowerCase())
                      : [...prev, tag]
                  )}
                  data-testid={`stage-desire-${tag.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`text-[11px] uppercase tracking-wider px-2.5 py-1 border transition-colors ${
                    active
                      ? "bg-rose-600 border-rose-600 text-white"
                      : "bg-black/30 border-white/10 text-zinc-300 hover:border-rose-400"
                  }`}
                >
                  {tag}
                </button>
              );
            })}
            {desireCatalog.length === 0 && (
              <div className="text-xs text-zinc-500 py-2">Loading tags…</div>
            )}
          </div>
        </div>
      )}

      {config === null && (
        <div className="mb-5 border-l-2 border-zinc-600 pl-4 py-2 text-xs text-zinc-500" data-testid="live-config-connecting">
          Connecting to live services…
        </div>
      )}

      {config !== null && !config.configured && (
        <div className="mb-5 border-l-2 border-amber-500 pl-4 py-3 text-sm text-zinc-300 bg-amber-500/5" data-testid="live-demo-mode-banner">
          <div className="text-xs uppercase tracking-widest text-amber-500 mb-1">Live streaming demo mode</div>
          <div className="text-zinc-400">
            LiveKit isn't configured yet — set <code className="text-white">LIVEKIT_URL</code>,{" "}
            <code className="text-white">LIVEKIT_API_KEY</code>, and{" "}
            <code className="text-white">LIVEKIT_API_SECRET</code> in the backend to enable real camera streaming.
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="card-surface p-12 text-center" data-testid="stage-empty">
          <div className="font-display text-2xl mb-2">
            {desireFilters.length > 0
              ? `No live rooms match your ${desireFilters.length} desire ${desireFilters.length === 1 ? "tag" : "tags"} right now.`
              : `Nobody's live in ${category === "All" ? "the vault" : category} right now.`}
          </div>
          <div className="text-sm text-zinc-500 mb-6">
            {desireFilters.length > 0 ? "Try loosening the filter or check back soon." : "Kick things off — start streaming."}
          </div>
          {desireFilters.length > 0 && (
            <Button
              onClick={() => setDesireFilters([])}
              className="rounded-full mr-2 bg-transparent border border-white/15 text-white hover:border-rose-600"
              data-testid="stage-empty-clear-desires"
            >
              Clear desire filter
            </Button>
          )}
          {user && (
            <Button onClick={openGoLive} className="btn-crimson rounded-full">Go Live</Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {hero && <StageHero stream={hero} onOpen={setActive} />}
          {rest.length > 0 && (
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {rest.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setActive(s)}
                  className="text-left"
                  data-testid={`live-open-${s.id}`}
                >
                  <LiveCard stream={s} />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {active && (
        <LiveRoom
          stream={active}
          onClose={closeRoom}
          onSurf={(nextStream) => {
            setActive(nextStream);
            setParams({ s: nextStream.id });
          }}
        />
      )}

      {/* Full-screen TikTok-style vertical feed — only when Surf mode is on and no room is open */}
      {surfMode && !active && filtered.length > 0 && (
        <VerticalLiveFeed
          streams={filtered}
          onJoin={setActive}
          onClose={() => {
            setSurfMode(false);
            try { window.localStorage.setItem("pandax_surf_mode", "0"); } catch { /* noop */ }
          }}
        />
      )}
    </div>
  );
}
